import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Wallet types
const WALLET_TYPES = {
    METAMASK: 'metamask',
    PHANTOM: 'phantom',
    COINBASE: 'coinbase',
    TRUST: 'trust',
    WALLETCONNECT: 'walletconnect',
};

// Wallet configuration
const SUPPORTED_WALLETS = [
    {
        id: WALLET_TYPES.METAMASK,
        name: 'MetaMask',
        icon: '🦊',
        color: '#E2761B',
        description: 'Connect using MetaMask browser extension',
        checkInstalled: () => typeof window !== 'undefined' && window.ethereum?.isMetaMask,
    },
    {
        id: WALLET_TYPES.PHANTOM,
        name: 'Phantom',
        icon: '👻',
        color: '#AB9FF2',
        description: 'Connect using Phantom wallet',
        checkInstalled: () => typeof window !== 'undefined' && window.solana?.isPhantom,
    },
    {
        id: WALLET_TYPES.COINBASE,
        name: 'Coinbase Wallet',
        icon: '💰',
        color: '#0052FF',
        description: 'Connect using Coinbase Wallet',
        checkInstalled: () => typeof window !== 'undefined' && window.ethereum?.isCoinbaseWallet,
    },
    {
        id: WALLET_TYPES.TRUST,
        name: 'Trust Wallet',
        icon: '🛡️',
        color: '#3375BB',
        description: 'Connect using Trust Wallet',
        checkInstalled: () => typeof window !== 'undefined' && window.ethereum?.isTrust,
    },
    {
        id: WALLET_TYPES.WALLETCONNECT,
        name: 'WalletConnect',
        icon: '🔗',
        color: '#3B99FC',
        description: 'Scan with WalletConnect compatible wallet',
        checkInstalled: () => true, // Always available
    },
];

// Context
const WalletContext = createContext(null);

export const useWallet = () => {
    const context = useContext(WalletContext);
    if (!context) {
        throw new Error('useWallet must be used within a WalletProvider');
    }
    return context;
};

export const WalletProvider = ({ children }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [address, setAddress] = useState(null);
    const [balance, setBalance] = useState(null);
    const [walletType, setWalletType] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Format address for display
    const formatAddress = (addr) => {
        if (!addr) return '';
        return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    };

    // Connect to MetaMask
    const connectMetaMask = async () => {
        if (!window.ethereum?.isMetaMask) {
            window.open('https://metamask.io/download/', '_blank');
            throw new Error('MetaMask is not installed');
        }

        const accounts = await window.ethereum.request({
            method: 'eth_requestAccounts',
        });

        const balanceWei = await window.ethereum.request({
            method: 'eth_getBalance',
            params: [accounts[0], 'latest'],
        });

        const balanceEth = parseInt(balanceWei, 16) / 1e18;

        return {
            address: accounts[0],
            balance: balanceEth.toFixed(4),
        };
    };

    // Connect to Phantom (Solana)
    const connectPhantom = async () => {
        if (!window.solana?.isPhantom) {
            window.open('https://phantom.app/', '_blank');
            throw new Error('Phantom is not installed');
        }

        const response = await window.solana.connect();
        const publicKey = response.publicKey.toString();

        // Balance will show as 0 - real balance check requires @solana/web3.js
        let balance = '0.00';

        return {
            address: publicKey,
            balance,
        };
    };

    // Connect to Coinbase Wallet
    const connectCoinbase = async () => {
        if (!window.ethereum?.isCoinbaseWallet) {
            window.open('https://www.coinbase.com/wallet', '_blank');
            throw new Error('Coinbase Wallet is not installed');
        }

        const accounts = await window.ethereum.request({
            method: 'eth_requestAccounts',
        });

        const balanceWei = await window.ethereum.request({
            method: 'eth_getBalance',
            params: [accounts[0], 'latest'],
        });

        const balanceEth = parseInt(balanceWei, 16) / 1e18;

        return {
            address: accounts[0],
            balance: balanceEth.toFixed(4),
        };
    };

    // Connect wallet
    const connectWallet = useCallback(async (type) => {
        setIsConnecting(true);
        setError(null);

        try {
            let result;

            switch (type) {
                case WALLET_TYPES.METAMASK:
                    result = await connectMetaMask();
                    break;
                case WALLET_TYPES.PHANTOM:
                    result = await connectPhantom();
                    break;
                case WALLET_TYPES.COINBASE:
                    result = await connectCoinbase();
                    break;
                case WALLET_TYPES.TRUST:
                    result = await connectMetaMask(); // Trust uses same interface
                    break;
                case WALLET_TYPES.WALLETCONNECT:
                    // WalletConnect requires additional setup
                    throw new Error('WalletConnect requires project ID configuration');
                default:
                    throw new Error('Unsupported wallet type');
            }

            setAddress(result.address);
            setBalance(result.balance);
            setWalletType(type);
            setIsConnected(true);
            setIsModalOpen(false);

            // Save to localStorage
            localStorage.setItem('walletConnected', 'true');
            localStorage.setItem('walletType', type);
            localStorage.setItem('walletAddress', result.address);

        } catch (err) {
            setError(err.message);
            console.error('Wallet connection error:', err);
        } finally {
            setIsConnecting(false);
        }
    }, []);

    // Disconnect wallet
    const disconnectWallet = useCallback(() => {
        setIsConnected(false);
        setAddress(null);
        setBalance(null);
        setWalletType(null);
        setError(null);

        localStorage.removeItem('walletConnected');
        localStorage.removeItem('walletType');
        localStorage.removeItem('walletAddress');

        // Disconnect from Phantom if connected
        if (window.solana?.isPhantom) {
            window.solana.disconnect();
        }
    }, []);

    // Open/close modal
    const openModal = useCallback(() => setIsModalOpen(true), []);
    const closeModal = useCallback(() => setIsModalOpen(false), []);

    // Auto-reconnect on page load
    useEffect(() => {
        const savedWalletType = localStorage.getItem('walletType');
        const savedAddress = localStorage.getItem('walletAddress');
        const wasConnected = localStorage.getItem('walletConnected');

        if (wasConnected === 'true' && savedWalletType && savedAddress) {
            // Try to reconnect silently
            connectWallet(savedWalletType).catch(() => {
                // If reconnection fails, clear storage
                localStorage.removeItem('walletConnected');
                localStorage.removeItem('walletType');
                localStorage.removeItem('walletAddress');
            });
        }
    }, [connectWallet]);

    // Listen for account changes
    useEffect(() => {
        if (window.ethereum) {
            const handleAccountsChanged = (accounts) => {
                if (accounts.length === 0) {
                    disconnectWallet();
                } else if (accounts[0] !== address) {
                    setAddress(accounts[0]);
                    localStorage.setItem('walletAddress', accounts[0]);
                }
            };

            window.ethereum.on('accountsChanged', handleAccountsChanged);
            return () => {
                window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
            };
        }
    }, [address, disconnectWallet]);

    const value = {
        isConnected,
        address,
        balance,
        walletType,
        isConnecting,
        error,
        isModalOpen,
        formatAddress,
        connectWallet,
        disconnectWallet,
        openModal,
        closeModal,
        supportedWallets: SUPPORTED_WALLETS,
        WALLET_TYPES,
    };

    return (
        <WalletContext.Provider value={value}>
            {children}
        </WalletContext.Provider>
    );
};

export default WalletContext;
